import { 
  Target, Lightbulb, Users, MapPin, Calendar, Trophy, 
  Building2, TrendingUp, Zap, AlertTriangle, FileText,
  Briefcase, Heart, Rocket, UserSearch
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CampaignKnowledge, CampaignGoals } from "./types";

interface CampaignKnowledgeDetailsProps {
  knowledge: CampaignKnowledge;
}

export function CampaignKnowledgeDetails({ knowledge }: CampaignKnowledgeDetailsProps) {
  const campaignGoals = (knowledge.campaign_goals || {}) as CampaignGoals;

  const renderField = (label: string, value: string | string[] | undefined, icon: React.ReactNode) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
          <div className="text-muted-foreground">{icon}</div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-sm text-muted-foreground/50 italic">Not yet collected</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
        <div className="text-primary">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          {Array.isArray(value) ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {value.map((v, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground whitespace-pre-wrap">{value}</p>
          )}
        </div>
      </div>
    );
  };

  const formatCampaignPurpose = (purpose?: string) => {
    const purposeMap: Record<string, string> = {
      sales: "Sales & Client Acquisition",
      hiring: "Hiring & Recruitment",
      partner: "Partnership Development",
      branding: "Brand Building",
      job_hunting: "Job Search",
    };
    return purpose ? purposeMap[purpose] || purpose : undefined;
  };

  const isSalesPath = campaignGoals.campaign_purpose === "sales" || !campaignGoals.campaign_purpose;
  const isHiringPath = campaignGoals.campaign_purpose === "hiring";
  const isPartnerPath = campaignGoals.campaign_purpose === "partner";
  const isBrandingPath = campaignGoals.campaign_purpose === "branding";
  const isJobHuntingPath = campaignGoals.campaign_purpose === "job_hunting";

  return (
    <div className="space-y-6">
      {/* Phase 1: The Big Goal */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">1</span>
          The Big Goal
        </h3>
        <div className="grid gap-3">
          {renderField("Campaign Purpose", formatCampaignPurpose(campaignGoals.campaign_purpose), <Target className="h-4 w-4" />)}
          {renderField("Success Definition", campaignGoals.success_definition, <Trophy className="h-4 w-4" />)}
        </div>
      </div>

      {/* Phase 2: The Offer */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">2</span>
          The Offer
        </h3>
        <div className="grid gap-3">
          {isSalesPath && (
            <>
              {renderField("Target Department", campaignGoals.target_department, <Building2 className="h-4 w-4" />)}
              {renderField("Pain If No Service", campaignGoals.pain_if_no_service, <AlertTriangle className="h-4 w-4" />)}
              {renderField("First CTA", campaignGoals.first_cta, <Zap className="h-4 w-4" />)}
              {renderField("Why They Should Reply", campaignGoals.why_reply, <Lightbulb className="h-4 w-4" />)}
            </>
          )}
          {isHiringPath && (
            <>
              {renderField("Candidate Seniority", campaignGoals.candidate_seniority, <Briefcase className="h-4 w-4" />)}
              {renderField("Team Attraction", campaignGoals.team_attraction, <Lightbulb className="h-4 w-4" />)}
              {renderField("Company Culture", campaignGoals.company_culture, <Heart className="h-4 w-4" />)}
            </>
          )}
          {isPartnerPath && (
            <>
              {renderField("Partnership Type", campaignGoals.partner_type, <Users className="h-4 w-4" />)}
              {renderField("Business Milestone", campaignGoals.business_milestone, <Lightbulb className="h-4 w-4" />)}
            </>
          )}
          {isBrandingPath && (
            <>
              {renderField("Content Type", campaignGoals.content_type, <FileText className="h-4 w-4" />)}
              {renderField("Thought Leadership", campaignGoals.thought_leadership, <Heart className="h-4 w-4" />)}
            </>
          )}
          {isJobHuntingPath && (
            <>
              {renderField("Job Type", campaignGoals.job_type, <Briefcase className="h-4 w-4" />)}
              {renderField("Unique Value", campaignGoals.unique_value, <Rocket className="h-4 w-4" />)}
            </>
          )}
        </div>
      </div>

      {/* Phase 3: Ideal Person */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">3</span>
          Ideal Person
        </h3>
        <div className="grid gap-3">
          {renderField("Target Location", campaignGoals.target_location, <MapPin className="h-4 w-4" />)}
          {renderField("Target Seniority", campaignGoals.target_seniority, <Users className="h-4 w-4" />)}
          {renderField("Best Client Example", campaignGoals.best_client_example, <UserSearch className="h-4 w-4" />)}
        </div>
      </div>

      {/* Phase 4: Campaign Limits */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">4</span>
          Campaign Limits
        </h3>
        <div className="grid gap-3">
          {renderField("Weekly Volume", campaignGoals.weekly_volume, <Calendar className="h-4 w-4" />)}
        </div>
      </div>

      {/* Phase 5: Social Proof */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">5</span>
          Social Proof
        </h3>
        <div className="grid gap-3">
          {renderField("Biggest Win", campaignGoals.biggest_win, <Trophy className="h-4 w-4" />)}
          {renderField("Reference Client", campaignGoals.reference_client, <Building2 className="h-4 w-4" />)}
          {renderField("Content Link", campaignGoals.content_link, <FileText className="h-4 w-4" />)}
        </div>
      </div>

      {/* Phase 6: Target Understanding */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">6</span>
          Target Understanding
        </h3>
        <div className="grid gap-3">
          {renderField("Common Traits", campaignGoals.common_traits, <Users className="h-4 w-4" />)}
          {renderField("Special Requirements", campaignGoals.special_requirements, <AlertTriangle className="h-4 w-4" />)}
        </div>
      </div>

      {/* Phase 7: Tracking & Metrics */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">7</span>
          Tracking & Metrics
        </h3>
        <div className="grid gap-3">
          {renderField("Primary KPI", campaignGoals.primary_kpi, <TrendingUp className="h-4 w-4" />)}
          {renderField("Rejection Handling", campaignGoals.rejection_handling, <AlertTriangle className="h-4 w-4" />)}
        </div>
      </div>
    </div>
  );
}
